import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {InteractiveDateTimeFormatterContainer} from '../../../../../components/common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {RandomSkeleton} from '../../../../../components/common/randomSkeleton';
import {Skeleton} from '../../../../../components/common/skeleton';
import {ActivityRulePillContainer} from '../../../../../components/inboxes/viewer/activity/activityRulePill/activityRulePillContainer';
import {
  AnalyticsTimelineEvent,
  AnalyticsTimelineEventsEnum,
  AnalyticsTimelineEventSource,
  filterMessageReceivers,
} from '../../../../../models/api/analyticsTimelineEventModel';
import {CardModel} from '../../../../../models/api/cardModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {ResourceMap} from '../../../../../store/appTypes';
import {ConversationCardTag} from '../conversations/conversationCardTag';
import {TimelineMessagePreview} from './timelineMessagePreview';

/*
 * Styles.
 */

const StyledEventDiv = styled.div`
  grid-area: event;
  overflow: hidden;
`;

const StyledShowHiddenEventsContainerDiv = styled.div`
  grid-area: button;
  width: fit-content;
`;

const StyledEventDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  ${ellipsis()}
`;

const StyledEventDescriptionWrapperDiv = styled.div`
  display: flex;
`;

const StyledIconCircleDiv = styled.div`
  grid-area: icon;

  height: 24px;
  width: 24px;
  background-color: ${(p) => p.theme.greys.shade40};
  border-radius: 50%;

  ${flexCenter};
  justify-content: center;
`;

const StyledHiddenEventsCircleDiv = styled.div`
  grid-area: icon;

  height: 8px;
  width: 8px;
  margin: 8px;
  background-color: ${(p) => p.theme.greys.shade40};
  border-radius: 50%;

  ${flexCenter};
  justify-content: center;
`;

const StyledEventTeammateAndDateDiv = styled.div`
  display: flex;
  margin-top: 4px;
  margin-bottom: 2px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledEventTeammateDiv = styled.div`
  ${ellipsis()}
`;

const StyledEventBodyDiv = styled.div`
  grid-area: body;
  overflow: hidden;
  padding-bottom: 8px;
`;

const StyledBoldEventDescriptionDiv = styled.div`
  font-weight: ${fontWeights.bold};
  margin-right: 4px;
`;

const StyledEventDateDiv = styled.div`
  flex-shrink: 0;
`;

const StyledSkeletonIcon = styled(Skeleton)`
  grid-area: icon;
`;

const StyledRandomSkeletonWrapperDiv = styled.div`
  grid-area: event;
`;

/*
 * Props.
 */

interface RenderEventProps {
  eventIcon: IconName;
  eventDescription: ReactNode;
  eventBody?: ReactNode;
  eventDate: number;
  eventSource?: AnalyticsTimelineEventSource;
  tag?: TagModel;
}

/*
 * Constants.
 */

export const timelineLoadingItem = (
  <>
    <StyledSkeletonIcon width="24px" height="24px" borderRadius="50%" />
    <StyledRandomSkeletonWrapperDiv>
      <RandomSkeleton height="24px" />
    </StyledRandomSkeletonWrapperDiv>
  </>
);

/*
 * Helpers.
 */

function renderEventDescription(eventDescription: ReactNode, tag?: TagModel) {
  return (
    <StyledEventDescriptionWrapperDiv>
      {eventDescription}
      {tag && <ConversationCardTag tag={tag} />}
    </StyledEventDescriptionWrapperDiv>
  );
}

function renderEventSourceAndDate(eventDate: number, eventSource?: AnalyticsTimelineEventSource) {
  return (
    <StyledEventTeammateAndDateDiv>
      {eventSource && renderEventSource(eventSource)}
      <StyledEventDateDiv>
        <InteractiveDateTimeFormatterContainer value={eventDate} />
      </StyledEventDateDiv>
    </StyledEventTeammateAndDateDiv>
  );
}

function renderEventSource(eventSource: AnalyticsTimelineEventSource) {
  if (eventSource.ruleId && eventSource.label) {
    return (
      <>
        <ActivityRulePillContainer
          ruleName={eventSource.label}
          ruleId={eventSource.ruleId}
          ruleBundleId={eventSource.ruleBundleId}
          ruleBundleType={eventSource.ruleBundleType}
          ruleNamespace={eventSource.ruleNamespace}
        />
        <div>&nbsp;{'\u2022'}&nbsp;</div>
      </>
    );
  }
  return (
    <>
      <StyledEventTeammateDiv>{eventSource.label}</StyledEventTeammateDiv>
      <div>&nbsp;{'\u2022'}&nbsp;</div>
    </>
  );
}

function maybeRenderEventBody(eventBody: ReactNode) {
  if (!eventBody) {
    return null;
  }

  return <StyledEventBodyDiv>{eventBody}</StyledEventBodyDiv>;
}

function renderEvent({
  eventIcon,
  eventSource,
  eventDescription,
  eventBody,
  eventDate,
  tag,
}: RenderEventProps) {
  return (
    <>
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconCircleDiv>
            <Icon name={eventIcon} size={14} color={theme.greys.shade70} />
          </StyledIconCircleDiv>
        )}
      </UserThemeConsumer>
      <StyledEventDiv>
        {renderEventDescription(eventDescription, tag)}
        {renderEventSourceAndDate(eventDate, eventSource)}
      </StyledEventDiv>
      {maybeRenderEventBody(eventBody)}
    </>
  );
}

/*
 * Renders a single timeline event.
 * NOTE: We pass down a full list of messages and cards here because the `timelineEvent` only has references to the resource UIDs.
 * Therefore, we do a reverse lookup in order to render the necessary resources correctly for `inbound`, `outbound`, and `out_reply` events.
 */
export function renderTimelineEvent(
  timelineEvent: AnalyticsTimelineEvent,
  messagesResourcesMap: ResourceMap<MessageModel>,
  cardsResourcesMap: ResourceMap<CardModel>,
) {
  switch (timelineEvent.type) {
    case AnalyticsTimelineEventsEnum.ARCHIVE:
      return renderEvent({
        eventIcon: 'archiveActivityFilled',
        eventSource: timelineEvent.archivedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-archived">
            <StyledBoldEventDescriptionDiv>Archived</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>in {timelineEvent.archivedIn}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.ASSIGN:
      return renderEvent({
        eventIcon: 'assignActivityFilled',
        eventSource: timelineEvent.assignedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-assigned">
            <StyledBoldEventDescriptionDiv>Assigned</StyledBoldEventDescriptionDiv>
            {timelineEvent.assignedTo && (
              <StyledEventDescriptionDiv>to {timelineEvent.assignedTo}</StyledEventDescriptionDiv>
            )}
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.UNASSIGN:
      return renderEvent({
        eventIcon: 'unassignActivityFilled',
        eventSource: timelineEvent.unassignedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-unassigned">
            <StyledBoldEventDescriptionDiv>Unassigned</StyledBoldEventDescriptionDiv>
            {timelineEvent.unassignedFrom && (
              <StyledEventDescriptionDiv>from {timelineEvent.unassignedFrom}</StyledEventDescriptionDiv>
            )}
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.INBOUND: {
      const {message, fromCard, fallbackSenderHandle} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'inboxInboundFilled',
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-inbound">
            <StyledBoldEventDescriptionDiv>Received</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              message from {fromCard?.name ?? fallbackSenderHandle}
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.OUTBOUND: {
      const {message, fromCard, fallbackSenderHandle, sentTo} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
        true,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'sendFilled',
        eventSource: {label: fromCard?.name ?? fallbackSenderHandle},
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-outbound">
            <StyledBoldEventDescriptionDiv>Message</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>sent to {sentTo}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.OUT_REPLY: {
      const {message, fromCard, fallbackSenderHandle, sentTo} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
        true,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'reply',
        eventSource: {label: fromCard?.name ?? fallbackSenderHandle},
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-out-reply">
            <StyledBoldEventDescriptionDiv>Reply</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>sent to {sentTo}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.CONV_JOIN:
      return renderEvent({
        eventIcon: 'mergeActivityFilled',
        eventSource: timelineEvent.convJoinedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-conv-joined">
            <StyledBoldEventDescriptionDiv>Merged</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>the conversation</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.CONV_SPLIT:
      return renderEvent({
        eventIcon: 'splitActivityFilled',
        eventSource: timelineEvent.convSplittedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-conv-splitted">
            <StyledBoldEventDescriptionDiv>Split</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>the conversation</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.MOVE:
      return renderEvent({
        eventIcon: 'moveActivityFilled',
        eventSource: timelineEvent.movedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-moved">
            <StyledBoldEventDescriptionDiv>Moved</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>to {timelineEvent.movedIn}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.REMINDER:
      return renderEvent({
        eventIcon: 'snoozeActivityFilled',
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-reminder">
            <StyledBoldEventDescriptionDiv>Snoozed</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>expired</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.REOPEN:
      return renderEvent({
        eventIcon: 'unarchiveActivityFilled',
        eventSource: timelineEvent.reopenedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-reopened">
            <StyledBoldEventDescriptionDiv>Reopened</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>in {timelineEvent.reopenedIn}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.SNOOZE:
      // TODO: Fetch snooze expiration date using reminder_id.
      return renderEvent({
        eventIcon: 'snoozeActivityFilled',
        eventSource: timelineEvent.snoozedBy,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-snoozed">Snoozed</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.TAG:
      return renderEvent({
        eventIcon: 'tagActivityFilled',
        eventSource: timelineEvent.taggedBy,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-tag">Added</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        tag: timelineEvent.tag,
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.UNTAG:
      return renderEvent({
        eventIcon: 'untagActivityFilled',
        eventSource: timelineEvent.untaggedBy,
        tag: timelineEvent.tag,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-untagged">Removed</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        eventDate: timelineEvent.date,
      });

    default:
      throw new Error('This should never happen');
  }
}

export function renderHiddenEventsButton(hiddenEventsCount: number, onClick: Handler) {
  return (
    <>
      <StyledHiddenEventsCircleDiv />
      <StyledShowHiddenEventsContainerDiv>
        <Button variant={ButtonVariantsEnum.LINK} size={VisualSizesEnum.SMALL} onClick={onClick}>
          {`${hiddenEventsCount} `}
          <Plural
            id="analytics-timeline-show-hidden-events"
            value={hiddenEventsCount}
            one="event hidden"
            other="events hidden"
          />
        </Button>
      </StyledShowHiddenEventsContainerDiv>
    </>
  );
}

/*
 * Helpers specific to the inbound, outbound and out_reply events.
 */

// Get the Card of the message sender. We return the first card that has a matching contactUid.
function retrieveSenderCardFromMessage(message: MessageModel, cardsResourcesMap: ResourceMap<CardModel>) {
  const fromRecipient = message?.recipients.find((r) => r.role === RecipientRolesEnum.FROM); // Finding the FROM recipient.

  if (!fromRecipient?.contactUid) {
    return undefined;
  }

  const fromCard = cardsResourcesMap?.[fromRecipient.contactUid]; // Finding the correct card.

  return fromCard;
}

// From a messageUid will return the full Message and the sender's Card.
// For outbound and out_reply messages we also compute the "sentTo" string.
function retrieveFullMessageData(
  messageUid: string,
  messagesResourcesMap: ResourceMap<MessageModel>,
  cardsResourcesMap: ResourceMap<CardModel>,
  shouldProcessSentTo?: boolean,
) {
  const message = messagesResourcesMap[messageUid];
  const fromCard = message && retrieveSenderCardFromMessage(message, cardsResourcesMap);

  const fallbackSenderHandle =
    message?.recipients.find((r) => r.role === RecipientRolesEnum.FROM)?.handle || '';

  const sentTo =
    message &&
    shouldProcessSentTo &&
    filterMessageReceivers(message.recipients)
      .map((recipient) => recipient.name ?? recipient.handle)
      .join(', ');

  return {message, fromCard, sentTo, fallbackSenderHandle};
}
